import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DEPARTMENTS, DEPARTMENT_COLORS } from "@/constants";
import { format } from "date-fns";

export default function Reports() {
  const [status, setStatus] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [milestoneRep, setMilestoneRep] = useState(null);
  const [deptPerf, setDeptPerf] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [delayed, setDelayed] = useState(null);
  const [trends, setTrends] = useState(null);
  const [trendProject, setTrendProject] = useState("all");
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/reports/delay-trends", { params: trendProject !== "all" ? { project_id: trendProject } : {} }).then((r) => setTrends(r.data));
  }, [trendProject]);

  useEffect(() => {
    api.get("/reports/status").then((r) => setStatus(r.data));
    api.get("/reports/overdue").then((r) => setOverdue(r.data));
    api.get("/reports/milestones").then((r) => setMilestoneRep(r.data));
    api.get("/reports/department-performance").then((r) => setDeptPerf(r.data));
    api.get("/reports/alerts").then((r) => setAlerts(r.data));
    api.get("/reports/delayed-completions").then((r) => setDelayed(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  return (
    <div data-testid="reports-page">
      <span className="label-caps">Analytics</span>
      <h1 className="text-2xl font-display font-extrabold mt-1 mb-4">Reports</h1>

      <Tabs defaultValue="status">
        <TabsList className="w-full overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger data-testid="report-tab-status" value="status">Project Status</TabsTrigger>
          <TabsTrigger data-testid="report-tab-overdue" value="overdue">Overdue</TabsTrigger>
          <TabsTrigger data-testid="report-tab-delayed" value="delayed">Delayed Completions</TabsTrigger>
          <TabsTrigger data-testid="report-tab-delay-trends" value="delay-trends">Delay Trends</TabsTrigger>
          <TabsTrigger data-testid="report-tab-milestones" value="milestones">Milestones</TabsTrigger>
          <TabsTrigger data-testid="report-tab-department" value="department">Department Performance</TabsTrigger>
          <TabsTrigger data-testid="report-tab-alerts" value="alerts">Alert Log</TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          {status && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card-flat p-4 overflow-x-auto">
                <span className="label-caps">By Department</span>
                <table className="w-full text-sm mt-3">
                  <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Dept</th><th>Assigned</th><th>Done</th><th>Overdue</th><th>At Risk</th></tr></thead>
                  <tbody>{status.by_department.map((d) => (
                    <tr key={d.department} className="border-b border-border/50" data-testid={`status-dept-row-${d.department}`}>
                      <td className="py-2 font-medium">{d.department}</td><td>{d.assigned}</td><td>{d.completed}</td>
                      <td className="text-destructive">{d.overdue}</td><td className="text-amber-600">{d.at_risk}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="card-flat p-4 overflow-x-auto">
                <span className="label-caps">By Project</span>
                <table className="w-full text-sm mt-3">
                  <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Project</th><th>Completion</th><th>Health</th><th>Budget (₹)</th></tr></thead>
                  <tbody>{status.by_project.map((p) => (
                    <tr key={p.project_id} className="border-b border-border/50" data-testid={`status-project-row-${p.project_id}`}>
                      <td className="py-2 font-medium">{p.name}</td><td>{p.completion_pct}%</td>
                      <td className={p.timeline_health === "Late" ? "text-destructive" : "text-green-600"}>{p.timeline_health}</td>
                      <td className="font-mono text-xs">{p.budget ? p.budget.toLocaleString() : "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="overdue">
          {overdue && (
            <div className="card-flat p-4 overflow-x-auto">
              <span className="label-caps">{overdue.count} Overdue Tasks</span>
              <table className="w-full text-sm mt-3">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Task</th><th>Project</th><th>Department</th><th>Assignee</th><th>Due</th></tr></thead>
                <tbody>{overdue.tasks.map((t) => (
                  <tr key={t.id} className="border-b border-border/50" data-testid={`overdue-task-row-${t.id}`}>
                    <td className="py-2 font-medium">{t.title}</td><td>{t.project_name}</td><td>{t.department}</td><td>{t.assignee_name}</td>
                    <td className="font-mono text-xs text-destructive">{t.due_date ? format(new Date(t.due_date), "d MMM") : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
              {overdue.tasks.length === 0 && <div className="text-sm font-mono text-muted-foreground mt-2">&gt; no overdue tasks — great work</div>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="delayed">
          {delayed && (
            <div className="card-flat p-4 overflow-x-auto">
              <span className="label-caps">{delayed.count} Tasks Completed Late</span>
              <table className="w-full text-sm mt-3">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Task</th><th>Project</th><th>Department</th><th>Assignee</th><th>Due</th><th>Completed</th><th>Delay</th></tr></thead>
                <tbody>{delayed.tasks.map((t) => (
                  <tr key={t.id} className="border-b border-border/50" data-testid={`delayed-task-row-${t.id}`}>
                    <td className="py-2 font-medium">{t.title}</td><td>{t.project_name}</td><td>{t.department}</td><td>{t.assignee_name}</td>
                    <td className="font-mono text-xs">{format(new Date(t.due_date), "d MMM")}</td>
                    <td className="font-mono text-xs">{format(new Date(t.actual_completion_date), "d MMM")}</td>
                    <td className="font-mono text-xs font-semibold text-amber-600">{t.delay_days}d late</td>
                  </tr>
                ))}</tbody>
              </table>
              {delayed.tasks.length === 0 && <div className="text-sm font-mono text-muted-foreground mt-2">&gt; no delayed completions — great work</div>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="delay-trends">
          <div data-testid="delay-trends-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <span className="label-caps">Delay-days over time, by department</span>
              <div className="w-64">
                <Select value={trendProject} onValueChange={setTrendProject}>
                  <SelectTrigger data-testid="delay-trends-project-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {trends && trends.total_items > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-flat p-4 lg:col-span-2">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={trends.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" fontSize={11} />
                      <YAxis fontSize={11} label={{ value: "delay days", angle: -90, position: "insideLeft", fontSize: 10 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {DEPARTMENTS.map((d) => (
                        <Bar key={d} dataKey={d} stackId="delays" fill={DEPARTMENT_COLORS[d] || "#64748b"} radius={[0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="text-[11px] text-muted-foreground mt-2 font-mono">&gt; bucketed by the month each item was due · includes late completions + currently overdue tasks</p>
                </div>
                <div className="card-flat p-4 overflow-x-auto">
                  <span className="label-caps">Worst Departments</span>
                  <table className="w-full text-sm mt-3">
                    <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Dept</th><th>Late Items</th><th>Total</th><th>Avg</th></tr></thead>
                    <tbody>{trends.by_department.map((d, i) => (
                      <tr key={d.department} className="border-b border-border/50" data-testid={`delay-trend-dept-row-${d.department}`}>
                        <td className={`py-2 font-medium ${i === 0 ? "text-destructive" : ""}`}>
                          <span className="inline-block w-2.5 h-2.5 mr-1.5 align-middle" style={{ background: DEPARTMENT_COLORS[d.department] || "#64748b", borderRadius: "1px" }} />
                          {d.department}
                        </td>
                        <td>{d.late_count}</td>
                        <td className={`font-mono text-xs font-semibold ${i === 0 ? "text-destructive" : "text-amber-600"}`}>{d.total_delay_days}d</td>
                        <td className="font-mono text-xs">{d.avg_delay_days}d</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="card-flat p-6 text-sm font-mono text-muted-foreground" data-testid="delay-trends-empty">&gt; no delays recorded{trendProject !== "all" ? " for this project" : ""} — everything is on schedule</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="milestones">
          {milestoneRep && (
            <div className="card-flat p-4 overflow-x-auto">
              <div className="flex gap-6 mb-4 text-sm">
                <span data-testid="milestones-achieved-count"><b className="text-green-600">{milestoneRep.achieved}</b> Achieved</span>
                <span data-testid="milestones-missed-count"><b className="text-destructive">{milestoneRep.missed}</b> Missed</span>
                <span data-testid="milestones-pending-count"><b className="text-accent">{milestoneRep.pending}</b> Pending</span>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Milestone</th><th>Project</th><th>Target Date</th><th>Status</th></tr></thead>
                <tbody>{milestoneRep.items.map((m) => (
                  <tr key={m.id} className="border-b border-border/50" data-testid={`milestone-report-row-${m.id}`}>
                    <td className="py-2 font-medium">{m.name}</td><td>{m.project_name}</td>
                    <td className="font-mono text-xs">{m.target_date ? format(new Date(m.target_date), "d MMM yyyy") : "—"}</td><td>{m.status}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="department">
          {deptPerf && (
            <div className="card-flat p-4 overflow-x-auto">
              <span className="label-caps">On-Time Closure % by Department</span>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={deptPerf}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="department" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="closed_on_time_pct" fill="#38bdf8" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts">
          {alerts && (
            <div className="card-flat p-4 overflow-x-auto">
              <span className="label-caps">Alert Log ({alerts.length})</span>
              <table className="w-full text-sm mt-3">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Type</th><th>Subject</th><th>Recipient</th><th>Status</th><th>Sent</th></tr></thead>
                <tbody>{alerts.map((a) => (
                  <tr key={a.id} className="border-b border-border/50" data-testid={`alert-log-row-${a.id}`}>
                    <td className="py-2">{a.alert_type}</td><td>{a.subject}</td><td>{a.recipient_name}</td><td>{a.status}</td>
                    <td className="font-mono text-xs">{format(new Date(a.created_at), "d MMM, HH:mm")}</td>
                  </tr>
                ))}</tbody>
              </table>
              {alerts.length === 0 && <div className="text-sm font-mono text-muted-foreground mt-2">&gt; no alerts sent yet</div>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
